import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getBets, getPublicBets, getSchedule, respondToBet, cancelBet, resolveBet, claimBet, effectiveStakes, type BetRow } from '../api';
import PageHeader from '../components/PageHeader';
import ProposeBetSheet from '../components/ProposeBetSheet';
import { useAuth } from '../auth';
import type { EnrichedMatch } from '../types';
import { rigaDate, rigaTime } from '../format';
import { toast } from '../toast';
import { haptic } from '../haptic';

export default function Bets() {
  const { user } = useAuth();
  const [proposing, setProposing] = useState(false);

  const { data: pub } = useQuery({ queryKey: ['public-bets'], queryFn: getPublicBets, refetchInterval: 30_000 });
  const { data: mine } = useQuery({ queryKey: ['bets'], queryFn: getBets, enabled: !!user });
  const { data: schedule } = useQuery({ queryKey: ['schedule'], queryFn: getSchedule });

  const matchById = useMemo(() => {
    const map = new Map<string, EnrichedMatch>();
    for (const m of schedule?.matches ?? []) map.set(m.id, m);
    return map;
  }, [schedule]);

  const publicBets = pub?.bets ?? [];
  const myBets = mine?.bets ?? [];
  const incoming = myBets.filter((b) => b.opponent === user && b.status === 'pending');
  const sent = myBets.filter((b) => b.proposer === user && b.status === 'pending');
  const myOpenMarketplace = myBets.filter((b) => b.proposer === user && b.status === 'open');
  const active = myBets.filter((b) => b.status === 'active');
  const declined = myBets.filter((b) => b.status === 'declined');
  const cancelled = myBets.filter((b) => b.status === 'cancelled' || b.status === 'voided');
  const settled = myBets.filter((b) => b.status === 'settled');
  // Marketplace shows ALL open bets across the league (so signed-in users can claim others').
  const marketplace = publicBets.filter((b) => b.status === 'open');

  return (
    <>
      <PageHeader
        title="Bets"
        subtitle={user ? 'Custom bets between players. Both confirm the winner once settled.' : 'Sign in (top right) to place bets.'}
        right={user && (
          <button
            onClick={() => setProposing(true)}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-pitch-500 text-ink-950 font-semibold text-xs hover:bg-pitch-400 active:bg-pitch-600"
          >
            <span className="text-base leading-none">＋</span> New
          </button>
        )}
      />

      {user && mine?.ledger && mine.ledger.length > 0 && (
        <section className="px-4 lg:px-0 mb-4">
          <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Your ledger</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {mine.ledger.map((l) => (
              <div key={l.opponent} className="card px-3 py-2 flex items-center justify-between">
                <div>
                  <div className="text-xs text-dim">vs</div>
                  <div className="text-sm font-semibold">{l.opponent}</div>
                  <div className="text-[10px] text-dim">{l.wins}W · {l.losses}L</div>
                </div>
                <div className={`stat text-lg font-semibold ${l.net > 0 ? 'text-pitch-400' : l.net < 0 ? 'text-red-400' : 'text-dim'}`}>
                  {l.net > 0 ? '+' : ''}€{l.net}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="px-4 lg:px-0 space-y-2">
        {user ? (
          <>
            <Group title="Marketplace · open bets" tone="pitch" count={marketplace.length} bets={marketplace} matchById={matchById} me={user} defaultOpen={marketplace.length > 0} hideEmpty />
            <Group title="Incoming for you" tone="pitch" count={incoming.length} bets={incoming} matchById={matchById} me={user} defaultOpen={incoming.length > 0} />
            <Group title="Your pending proposals" count={sent.length} bets={sent} matchById={matchById} me={user} defaultOpen={sent.length > 0} />
            <Group title="Your open marketplace posts" count={myOpenMarketplace.length} bets={myOpenMarketplace} matchById={matchById} me={user} defaultOpen={myOpenMarketplace.length > 0} hideEmpty />
            <Group title="Active" tone="pitch" count={active.length} bets={active} matchById={matchById} me={user} defaultOpen={active.length > 0} />
            <Group title="Declined" count={declined.length} bets={declined} matchById={matchById} me={user} defaultOpen={false} />
            <Group title="Settled" count={settled.length} bets={settled} matchById={matchById} me={user} defaultOpen={false} />
            <Group title="Cancelled / voided" count={cancelled.length} bets={cancelled} matchById={matchById} me={user} defaultOpen={false} />
            <Group title="League activity" count={publicBets.length} bets={publicBets} matchById={matchById} me={user} defaultOpen={myBets.length === 0} hideEmpty />
          </>
        ) : (
          <PublicBetsView bets={publicBets} matchById={matchById} />
        )}
      </div>

      <ProposeBetSheet open={proposing} onClose={() => setProposing(false)} />
    </>
  );
}

function PublicBetsView({ bets, matchById }: { bets: BetRow[]; matchById: Map<string, EnrichedMatch> }) {
  const pubMarketplace = bets.filter((b) => b.status === 'open');
  const pubActive = bets.filter((b) => b.status === 'active');
  const pubPending = bets.filter((b) => b.status === 'pending');
  const pubSettled = bets.filter((b) => b.status === 'settled');
  const pubOther = bets.filter((b) => ['declined', 'cancelled', 'voided'].includes(b.status));

  // Real bets only — active or settled (no pending, declined, cancelled, voided).
  const real = [...pubActive, ...pubSettled];
  const totalWagered = real.reduce((s, b) => s + effectiveStakes(b).proposer + effectiveStakes(b).opponent, 0);
  const biggestPot = real.reduce<{ bet: BetRow; pot: number } | null>((max, b) => {
    const st = effectiveStakes(b);
    const pot = st.proposer + st.opponent;
    return !max || pot > max.pot ? { bet: b, pot } : max;
  }, null);
  const pairCounts = new Map<string, number>();
  for (const b of real) {
    const k = [b.proposer, b.opponent].sort().join('::');
    pairCounts.set(k, (pairCounts.get(k) ?? 0) + 1);
  }
  let topPair: { a: string; b: string; n: number } | null = null;
  for (const [k, n] of pairCounts) {
    if (!topPair || n > topPair.n) {
      const [a, bp] = k.split('::');
      topPair = { a, b: bp, n };
    }
  }

  // Per-user betting statistics (only active bets — money still in play).
  const userStats = computeUserBettingStats(pubActive, pubSettled);

  return (
    <>
      {real.length > 0 && (
        <section className="card p-3 sm:p-4 mb-2">
          <h2 className="text-xs uppercase tracking-widest text-dim mb-2">League betting summary</h2>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <Stat label="On the table" value={`€${totalWagered}`} tone="pitch" />
            <Stat
              label="Biggest pot"
              value={biggestPot ? `€${biggestPot.pot}` : '—'}
              sub={biggestPot ? `${biggestPot.bet.proposer} vs ${biggestPot.bet.opponent}` : null}
            />
            <Stat label="Top rivalry" value={topPair ? `${topPair.n}` : '—'} sub={topPair ? `${topPair.a} vs ${topPair.b}` : null} />
          </div>
        </section>
      )}
      {userStats.length > 0 && (
        <section className="card p-3 sm:p-4 mb-2">
          <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Per-player exposure</h2>
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 gap-y-1 text-xs">
            <div className="text-[10px] uppercase tracking-wider text-dim">Player</div>
            <div className="text-[10px] uppercase tracking-wider text-dim text-right">At stake</div>
            <div className="text-[10px] uppercase tracking-wider text-dim text-right">Could win</div>
            <div className="text-[10px] uppercase tracking-wider text-dim text-right">Net so far</div>
            {userStats.map((s) => (
              <Row3
                key={s.user}
                name={s.user}
                stake={`€${s.atStake}`}
                payout={`€${s.couldWin}`}
                net={s.net}
              />
            ))}
          </div>
        </section>
      )}
      <Group title="Marketplace · open bets" tone="pitch" count={pubMarketplace.length} bets={pubMarketplace} matchById={matchById} me={null} defaultOpen={pubMarketplace.length > 0} hideEmpty />
      <Group title="Active" tone="pitch" count={pubActive.length} bets={pubActive} matchById={matchById} me={null} defaultOpen />
      <Group title="Pending" count={pubPending.length} bets={pubPending} matchById={matchById} me={null} defaultOpen={pubPending.length > 0 && pubActive.length === 0} />
      <Group title="Settled" count={pubSettled.length} bets={pubSettled} matchById={matchById} me={null} defaultOpen={pubActive.length === 0 && pubPending.length === 0} />
      <Group title="Declined / cancelled" count={pubOther.length} bets={pubOther} matchById={matchById} me={null} defaultOpen={false} />
    </>
  );
}

interface UserBettingStat { user: string; atStake: number; couldWin: number; net: number }

function computeUserBettingStats(active: BetRow[], settled: BetRow[]): UserBettingStat[] {
  const map = new Map<string, UserBettingStat>();
  const ensure = (u: string) => {
    let e = map.get(u);
    if (!e) { e = { user: u, atStake: 0, couldWin: 0, net: 0 }; map.set(u, e); }
    return e;
  };
  for (const b of active) {
    const st = effectiveStakes(b);
    const p = ensure(b.proposer);
    const o = ensure(b.opponent);
    p.atStake += st.proposer; p.couldWin += st.opponent;
    o.atStake += st.opponent; o.couldWin += st.proposer;
  }
  for (const b of settled) {
    if (!b.winner || b.winner === 'void') continue;
    const st = effectiveStakes(b);
    const proposerWon = b.winner === 'proposer';
    ensure(b.proposer).net += proposerWon ? st.opponent : -st.proposer;
    ensure(b.opponent).net += proposerWon ? -st.opponent : st.proposer;
  }
  return Array.from(map.values()).sort((a, b) => b.atStake - a.atStake || b.couldWin - a.couldWin);
}

function Row3({ name, stake, payout, net }: { name: string; stake: string; payout: string; net: number }) {
  return (
    <>
      <div className="font-semibold truncate">{name}</div>
      <div className="text-right font-mono text-red-400">{stake}</div>
      <div className="text-right font-mono text-pitch-400">{payout}</div>
      <div className={`text-right font-mono ${net > 0 ? 'text-pitch-400' : net < 0 ? 'text-red-400' : 'text-dim'}`}>{net > 0 ? '+' : ''}€{net}</div>
    </>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string | null; tone?: 'pitch' }) {
  return (
    <div className="text-center sm:text-left">
      <div className="text-[10px] uppercase tracking-wider text-dim">{label}</div>
      <div className={`stat text-lg font-semibold ${tone === 'pitch' ? 'text-pitch-400' : 'text-chalk'}`}>{value}</div>
      {sub && <div className="text-[10px] text-dim truncate">{sub}</div>}
    </div>
  );
}

function Group({ title, tone, count, bets, matchById, me, defaultOpen, hideEmpty }: {
  title: string;
  tone?: 'pitch';
  count: number;
  bets: BetRow[];
  matchById: Map<string, EnrichedMatch>;
  me: string | null;
  defaultOpen: boolean;
  hideEmpty?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (hideEmpty && count === 0) return null;

  return (
    <section className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 sm:px-4 py-2.5 text-left hover:bg-ink-800/40"
      >
        <span className="text-dim text-xs w-3">{open ? '▾' : '▸'}</span>
        <span className="text-sm font-semibold flex-1">{title}</span>
        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
          count === 0 ? 'bg-ink-800 text-dim'
          : tone === 'pitch' ? 'bg-pitch-500/15 text-pitch-400'
          : 'bg-ink-700 text-chalk'
        }`}>{count}</span>
      </button>
      {open && (
        count === 0 ? (
          <div className="px-4 pb-4 text-xs text-dim">Nothing here.</div>
        ) : (
          <div className="px-3 sm:px-4 pb-3 space-y-2">
            {bets.map((b) => (
              <BetCard
                key={b.id}
                bet={b}
                match={b.match_id ? matchById.get(b.match_id) : undefined}
                me={me}
              />
            ))}
          </div>
        )
      )}
    </section>
  );
}

function BetCard({ bet, match, me }: { bet: BetRow; match?: EnrichedMatch; me: string | null }) {
  const qc = useQueryClient();
  function patchStatus(action: 'accept' | 'decline' | 'cancel' | 'void' | 'won-me' | 'won-other') {
    const newStatus =
      action === 'accept' ? 'active'
      : action === 'decline' ? 'declined'
      : action === 'cancel' ? 'cancelled'
      : action === 'void' ? 'voided'
      : 'settled';
    const winner =
      action === 'won-me' ? (bet.proposer === me ? 'proposer' : 'opponent')
      : action === 'won-other' ? (bet.proposer === me ? 'opponent' : 'proposer')
      : action === 'void' ? 'void' : null;
    const patch = (b: BetRow): BetRow =>
      b.id !== bet.id ? b
      : { ...b, status: newStatus as BetRow['status'], winner: (winner as BetRow['winner']) };
    qc.setQueryData<{ user: string; bets: BetRow[]; ledger: unknown[] }>(['bets'], (old) =>
      old ? { ...old, bets: old.bets.map(patch) } : old,
    );
    qc.setQueryData<{ bets: BetRow[] }>(['public-bets'], (old) =>
      old ? { ...old, bets: old.bets.map(patch) } : old,
    );
  }

  const respond = useMutation({
    mutationFn: (action: 'accept' | 'decline') => respondToBet(bet.id, action),
    onMutate: (action) => { patchStatus(action); haptic.tap(); },
    onError: (e: Error) => { toast.error(e.message); qc.invalidateQueries({ queryKey: ['bets'] }); qc.invalidateQueries({ queryKey: ['public-bets'] }); },
    onSuccess: (_, action) => toast.success(action === 'accept' ? 'Bet accepted.' : 'Bet declined.'),
    onSettled: () => { qc.invalidateQueries({ queryKey: ['bets'] }); qc.invalidateQueries({ queryKey: ['public-bets'] }); },
  });
  const claim = useMutation({
    mutationFn: () => claimBet(bet.id),
    onSuccess: () => { haptic.success(); toast.success(`Claimed — you're now the opponent.`); },
    onError: (e: Error) => { haptic.error(); toast.error(e.message); },
    onSettled: () => { qc.invalidateQueries({ queryKey: ['bets'] }); qc.invalidateQueries({ queryKey: ['public-bets'] }); },
  });
  const cancel = useMutation({
    mutationFn: () => cancelBet(bet.id),
    onMutate: () => { patchStatus('cancel'); haptic.tap(); },
    onError: (e: Error) => { toast.error(e.message); qc.invalidateQueries({ queryKey: ['bets'] }); qc.invalidateQueries({ queryKey: ['public-bets'] }); },
    onSuccess: () => toast.success('Bet cancelled.'),
    onSettled: () => { qc.invalidateQueries({ queryKey: ['bets'] }); qc.invalidateQueries({ queryKey: ['public-bets'] }); },
  });
  const resolve = useMutation({
    mutationFn: (winner: 'proposer' | 'opponent' | 'void') => resolveBet(bet.id, winner),
    onMutate: (winner) => {
      const action = winner === 'void' ? 'void' : (winner === 'proposer' ? (bet.proposer === me ? 'won-me' : 'won-other') : (bet.opponent === me ? 'won-me' : 'won-other'));
      patchStatus(action);
      haptic.success();
    },
    onError: (e: Error) => { toast.error(e.message); qc.invalidateQueries({ queryKey: ['bets'] }); qc.invalidateQueries({ queryKey: ['public-bets'] }); },
    onSuccess: () => toast.success('Resolution recorded.'),
    onSettled: () => { qc.invalidateQueries({ queryKey: ['bets'] }); qc.invalidateQueries({ queryKey: ['public-bets'] }); },
  });

  const involved = !!me && (bet.proposer === me || bet.opponent === me);
  const youWin = me && bet.winner && ((bet.proposer === me && bet.winner === 'proposer') || (bet.opponent === me && bet.winner === 'opponent'));
  const youLose = me && bet.winner && ((bet.proposer === me && bet.winner === 'opponent') || (bet.opponent === me && bet.winner === 'proposer'));
  const stakes = effectiveStakes(bet);
  const isVariable = stakes.proposer !== stakes.opponent;
  const myClaim = me === bet.proposer ? bet.resolution_proposer_pick : me === bet.opponent ? bet.resolution_opponent_pick : null;
  const otherClaim = me === bet.proposer ? bet.resolution_opponent_pick : me === bet.opponent ? bet.resolution_proposer_pick : null;
  const sideLabel = bet.proposer_side ? (bet.proposer_side === 'yes' ? 'YES' : 'NO') : null;
  const oppSideLabel = bet.proposer_side ? (bet.proposer_side === 'yes' ? 'NO' : 'YES') : null;
  const isOpenMarket = bet.status === 'open' && bet.is_marketplace === 1;
  const canClaim = isOpenMarket && !!me && me !== bet.proposer;

  return (
    <article className="rounded-lg bg-ink-900/40 ring-1 ring-ink-800 p-3 sm:p-4 hover:ring-ink-700 transition-shadow">
      <Link to={`/bets/${bet.id}`} className="block">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[11px] flex-wrap">
              <span className="font-semibold">{bet.proposer}{sideLabel && <span className="text-pitch-400 ml-1">[{sideLabel}]</span>}</span>
              <span className="text-dim">vs</span>
              {isOpenMarket ? (
                <span className="font-semibold text-cyan-400">anyone{oppSideLabel && <span className="ml-1">[{oppSideLabel}]</span>}</span>
              ) : (
                <span className="font-semibold">{bet.opponent}{oppSideLabel && <span className="text-cyan-400 ml-1">[{oppSideLabel}]</span>}</span>
              )}
              <StatusPill status={bet.status} youWin={youWin} youLose={youLose} />
              {bet.is_marketplace === 1 && <span className="pill bg-cyan-500/15 text-cyan-400">Marketplace</span>}
              {isVariable && <span className="pill bg-ink-700 text-dim font-mono">€{stakes.proposer}:€{stakes.opponent}</span>}
              {isOpenMarket && bet.expires_at && (
                <span className="pill bg-ink-700 text-dim">exp {rigaDate(bet.expires_at)} {rigaTime(bet.expires_at)}</span>
              )}
              {isOpenMarket && !bet.expires_at && (
                <span className="pill bg-amber-500/15 text-amber-400">no expiry</span>
              )}
            </div>
            <div className="mt-1.5 text-sm leading-snug">{bet.description}</div>
            {match && (
              <div className="text-[11px] text-dim mt-1">
                {match.home_flag ?? '🏳️'} {match.home_name} vs {match.away_flag ?? '🏳️'} {match.away_name}
                {' · '}{rigaDate(match.utc_kickoff)} · {rigaTime(match.utc_kickoff)}
              </div>
            )}
            {bet.message && <div className="mt-1.5 text-xs text-dim italic">"{bet.message}"</div>}
          </div>
          <div className="text-right shrink-0">
            {isVariable ? (
              <>
                <div className="stat text-base font-semibold text-pitch-400">€{stakes.proposer}</div>
                <div className="stat text-base font-semibold text-cyan-400">€{stakes.opponent}</div>
                <div className="text-[10px] uppercase tracking-wider text-dim">stakes</div>
              </>
            ) : (
              <>
                <div className="stat text-xl font-semibold">€{stakes.proposer}</div>
                <div className="text-[10px] uppercase tracking-wider text-dim">stake</div>
              </>
            )}
          </div>
        </div>
      </Link>

      {(canClaim || (involved && (bet.status === 'pending' || bet.status === 'active')) || (isOpenMarket && bet.proposer === me)) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {canClaim && (
            <ActionBtn label={`Claim · risk €${stakes.opponent}`} onClick={() => claim.mutate()} primary disabled={claim.isPending} />
          )}
          {isOpenMarket && bet.proposer === me && (
            <ActionBtn label="Withdraw" onClick={() => cancel.mutate()} disabled={cancel.isPending} />
          )}
          {bet.status === 'pending' && bet.opponent === me && (
            <>
              <ActionBtn label="Accept" onClick={() => respond.mutate('accept')} primary disabled={respond.isPending} />
              <ActionBtn label="Decline" onClick={() => respond.mutate('decline')} disabled={respond.isPending} />
            </>
          )}
          {bet.status === 'pending' && bet.proposer === me && (
            <ActionBtn label="Cancel" onClick={() => cancel.mutate()} disabled={cancel.isPending} />
          )}
          {bet.status === 'active' && involved && (
            <SettlementButtons
              bet={bet} me={me!} myClaim={myClaim} otherClaim={otherClaim}
              resolve={(w) => resolve.mutate(w)} pending={resolve.isPending}
            />
          )}
        </div>
      )}
    </article>
  );
}

function SettlementButtons({ bet, me, myClaim, otherClaim, resolve, pending }: {
  bet: BetRow; me: string; myClaim: BetRow['winner']; otherClaim: BetRow['winner'];
  resolve: (w: 'proposer' | 'opponent' | 'void') => void; pending: boolean;
}) {
  const iAmProposer = bet.proposer === me;
  const meAsClaim: 'proposer' | 'opponent' = iAmProposer ? 'proposer' : 'opponent';
  const otherName = iAmProposer ? bet.opponent : bet.proposer;
  const otherAsClaim: 'proposer' | 'opponent' = iAmProposer ? 'opponent' : 'proposer';

  // If I've already claimed and opponent hasn't, waiting state.
  if (myClaim && !otherClaim) {
    return <div className="text-xs text-dim italic">Waiting for {otherName} to confirm.</div>;
  }
  // Opponent claimed first — show Approve / Dispute / Void.
  if (otherClaim && !myClaim) {
    const label = otherClaim === 'void' ? 'voided' : otherClaim === meAsClaim ? `${otherName} says you won` : `${otherName} says they won`;
    return (
      <div className="flex flex-col gap-1.5 w-full">
        <div className="text-xs text-dim">{label}. Confirm?</div>
        <div className="flex flex-wrap gap-2">
          <ActionBtn label="Approve" onClick={() => resolve(otherClaim)} primary disabled={pending} />
          <ActionBtn
            label="Dispute (I won)"
            onClick={() => resolve(meAsClaim)}
            disabled={pending}
          />
          {otherClaim !== 'void' && (
            <ActionBtn label="Void" onClick={() => resolve('void')} disabled={pending} />
          )}
        </div>
      </div>
    );
  }
  // Both claimed differently → contested
  if (myClaim && otherClaim && myClaim !== otherClaim) {
    return (
      <div className="flex flex-col gap-1.5 w-full">
        <div className="text-xs text-red-400">Contested: you said {claimLabel(myClaim, bet, me)}, {otherName} said {claimLabel(otherClaim, bet, me)}. Talk it out, then someone changes their pick.</div>
        <div className="flex flex-wrap gap-2">
          <ActionBtn label={`Change to "${otherName} won"`} onClick={() => resolve(otherAsClaim)} disabled={pending} />
          <ActionBtn label="Both agree it's void" onClick={() => resolve('void')} disabled={pending} />
        </div>
      </div>
    );
  }
  // Neither has claimed — fresh active state
  return (
    <>
      <ActionBtn label="I won" onClick={() => resolve(meAsClaim)} primary disabled={pending} />
      <ActionBtn label={`${otherName} won`} onClick={() => resolve(otherAsClaim)} disabled={pending} />
      <ActionBtn label="Void" onClick={() => resolve('void')} disabled={pending} />
    </>
  );
}

function claimLabel(claim: BetRow['winner'], bet: BetRow, me: string): string {
  if (claim === 'void') return 'void';
  const iAmProposer = bet.proposer === me;
  if (claim === 'proposer') return iAmProposer ? 'you won' : `${bet.proposer} won`;
  return iAmProposer ? `${bet.opponent} won` : 'you won';
}

function StatusPill({ status, youWin, youLose }: { status: string; youWin?: unknown; youLose?: unknown }) {
  if (status === 'settled') {
    if (youWin) return <span className="pill bg-pitch-500/20 text-pitch-400">You won</span>;
    if (youLose) return <span className="pill bg-red-500/20 text-red-400">You lost</span>;
    return <span className="pill bg-ink-700 text-chalk">Settled</span>;
  }
  const labels: Record<string, string> = {
    pending: 'Pending', active: 'Active', declined: 'Declined', cancelled: 'Cancelled', voided: 'Voided',
  };
  return <span className="pill bg-ink-700 text-dim">{labels[status] ?? status}</span>;
}

function ActionBtn({ label, onClick, primary, disabled }: { label: string; onClick: () => void; primary?: boolean; disabled?: boolean }) {
  return (
    <button
      onClick={onClick} disabled={disabled}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50 ${
        primary ? 'bg-pitch-500 text-ink-950 hover:bg-pitch-400' : 'bg-ink-800 text-chalk hover:bg-ink-700'
      }`}
    >{label}</button>
  );
}
